import React, { useContext } from 'react';
import { useSeo } from 'hooks/use-seo';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import ProductBestOffer from 'modules/product/views/components/product-best-offer';
import ProductAttributesList from 'modules/product/views/components/product-attributes-list/product-attributes-list';
import ProductPriceHistorySection from 'modules/product/views/components/product-price-history-section/product-price-history-section';
import Box from 'atomic/box';
import Rating from 'atomic/rating';
import Icon from 'atomic/icon';
import scrollToElement from 'modules/product/helpers/scroll-to-element';
import Link from 'atomic/link';
import { ProductInterface } from 'modules/product/interfaces/product.interface';
import ProductPhotoSection from 'modules/product/views/components/product-photo-section';
import Alert from 'modules/shared/components/alert';
import { CATEGORY_EXCEPTIONS_IDS } from 'modules/product/category-exceptions';
import getPromotedOffers from 'helpers/get-promoted-offers';

import './product-top-section.scss';

interface ProductTopSectionProps {
    product: Pick<ProductInterface, 'id' | 'name' | 'description' | 'photos' | 'attributes' | 'minPrice' | 'opinions'>
        & { category: Pick<ProductInterface['category'], 'name' | 'id'>; };
    productOffers: ProductInterface['offers'];
    onDescription?: () => void;
}

const SHORT_LENGTH_LIMIT = 230;

const ProductTopSection: React.FC<ProductTopSectionProps> = ({
    product,
    productOffers,
    onDescription
}) => {
    const seo = useSeo();
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';
    const h1 = seo.h1 || product.name;
    const shortDescriptionText = product.description.text !== 'null' ? String(product.description.text?.slice(0, SHORT_LENGTH_LIMIT)) : '';
    const totalDescriptionLength = product.description.text?.length ?? 0;
    const isDescriptionLarge = totalDescriptionLength > SHORT_LENGTH_LIMIT;
    const offer = getPromotedOffers(productOffers.items)[0] ?? productOffers.items[0];

    return (
        <Box className="product-top-section" padding={isMobile ? 'sm' : 'lg'}>
            <div className="image">
                <div className="image__carousel">
                    <ProductPhotoSection
                        product={product}
                        productOffers={productOffers}
                    />
                </div>
            </div>
            <div className="main">
                <div className="main__info">
                    {productOffers?.items[0]?.url && CATEGORY_EXCEPTIONS_IDS.includes(product.category.id)
                        ? (
                            <a
                                className="gtm_or_title"
                                href={productOffers?.items[0]?.url}
                                rel="nofollow noopener noreferrer"
                                target="_blank"
                                title="Sklep z najlepszą ofertą"
                            >
                                <h1 className="name">
                                    {h1}
                                </h1>
                            </a>
                        )
                        : (
                            <h1 className="name">
                                {h1}
                            </h1>
                        )}
                    <Rating
                        className="md product-top-section__rating"
                        opinions={product.opinions}
                        isAddOpinion={true}
                    />
                    {productOffers.count !== 0 && (
                        <>
                            <ProductBestOffer
                                title="Promowana oferta:"
                                offer={offer}
                                isMobile={isMobile}
                            />
                            <Link
                                href="#ceny"
                                type="button"
                                className="button-compare-offers"
                                color="transparent"
                                block={true}
                                size="lg"
                            >
                                Porównaj oferty ({productOffers.count})
                            </Link>
                        </>
                    )}
                    {productOffers.count === 0 && (
                        <div className="main__detail--no-offers">
                            <a
                                href="#ceny"
                                className="show-attributes"
                                onClick={(): void => scrollToElement('ceny')}
                            >
                                <Alert color="info">
                                    Na tę chwilę nie mamy aktywnych ofert dla tego produktu, sprawdź podobne produkty.
                                </Alert>
                            </a>
                        </div>
                    )}
                    { isMobile && (
                        <ProductPriceHistorySection className="main__price-history" productId={product.id} productPrice={product.minPrice} />
                    )}
                    { product.description.text && (
                        React.createElement(isDescriptionLarge ? 'a' : 'div', {
                            href: isDescriptionLarge ? '#opis' : void 0,
                            className: 'description',
                            onClick: isDescriptionLarge ? onDescription : void 0
                        }, (
                            <>
                                <span dangerouslySetInnerHTML={{ __html: shortDescriptionText }} />
                                {isDescriptionLarge && (
                                    <>...</>
                                )}
                            </>
                        ))
                    )}
                </div>
                { !isMobile && (
                    <ProductPriceHistorySection className="main__price-history" productId={product.id} productPrice={product.minPrice} />
                )}
                { product.attributes.length > 0 && (
                    <div className="main__detail">
                        <ProductAttributesList attributes={product.attributes} />
                        <a
                            href="#attributes"
                            className="show-attributes"
                            onClick={(): void => scrollToElement('attributes')}
                        >
                            Zobacz specyfikację
                            <Icon name="arrow-down-circle" color="primary" size={16} />
                        </a>
                    </div>
                )}
            </div>
        </Box>
    );
};

export default ProductTopSection;
